// src/lib/transferTabs.js
//
// #757, Session 79.22b.5.5c -- THE TRANSFERS TABS AS BINDER DIVIDERS. Colby's
// request at 79.22b.5.5b: "Think of how a binder has section dividers ... each
// tab resembles a piece of paper ... as you click on each tab that document
// comes to the top of the pile." Ruled there (R2-R4) and drawn here for his
// sign-off (R1, "approve as drawn, bar breaks"):
//   docs/audits/79.22b.5.5c/APPROVED_DRAWING_757_DESK.html (desk and tablet)
//   docs/audits/79.22b.5.5b/APPROVED_DRAWING_757_PHONE.html (the phone, M4)
//
// The rules a screen check cannot hold on its own live here, as plain functions,
// so a test can hold them: how tall a tab is, what its count looks like, which
// way the row can still scroll, and the phone row's order. The drawing lives in
// src/components/inventory/TransferTabs.js.

// THE CONTROL FLOOR (#417 D2, tiers.js): 40px with a finger, 32 by default with
// a mouse. The chosen tab stands 6px taller: 4px reach down over the coloured
// bar along the sheet's top edge, so the bar BREAKS under it and the tab and its
// sheet are one piece of paper (R1), and 2px lift it above the others.
export const TAB_BAR = 4;
export function tabHeights(coarse) {
  const base = coarse ? 40 : 32;
  return { base, chosen: base + TAB_BAR + 2 };
}

// THE SHEET, the white page the chosen tab is joined to. Its edge is counted by
// the desk's split rule (#755): the transfer on the right keeps its 440px
// minimum INSIDE the sheet, so the page needs these pixels too.
export const SHEET_BORDER = 1;
export const SHEET_PAD = 12;
// 7 on a phone, with the page's sides at 8 (TransfersPage.js): the sheet's inside
// is then exactly the 315px today's cards and tab sentence had at 375, so a
// one-line sentence stays on one line and the first card stays above gate 675
// D14's 222px. Measured at 79.22b.5.5c: at 8 the Awaiting confirmation sentence
// wrapped by 2px and the first card fell to 227.
export const SHEET_PAD_PHONE = 7;
export const SHEET_EDGES = 2 * (SHEET_BORDER + SHEET_PAD);
// Variant B's pile, verbatim from the drawing Colby picked it from: three sheet
// edges under the chosen one, and a soft shadow under those.
export const SHEET_PILE = '0 2px 0 -1px #fff, 0 3px 0 0 #DCDCDC, 0 6px 0 -1px #fff, 0 7px 0 0 #D2D2D2, '
  + '0 10px 0 -2px #fff, 0 11px 0 -1px #C8C8C8, 0 14px 18px rgba(0,0,0,0.10)';
// M4's sheet on a phone: one sheet, no pile (as approved).
export const SHEET_SHADOW_PHONE = '0 3px 10px rgba(0,0,0,0.08)';
export const HEADER_LINE = '#DCDCDC';

// THE COUNT ON A TAB. White on the chosen tab's own dark ink (#581: white sits
// on the darkened variant, never on the stage colour; 5.14-6.69); the stage's
// ink on white on the others. While a search is on the counts are matches
// (#675 D1): a tab holding matches carries the house green pair (5.48), and the
// chosen tab shows its count even at 0. Null: no pill.
export const MATCH_PAIR = Object.freeze({ bg: '#E6F4EE', fg: '#0F6E56' });
export function tabCountPill({ count = 0, on = false, searching = false, ink }) {
  if (!(count > 0 || (searching && on))) return null;
  if (on) return { bg: ink, fg: '#FFFFFF' };
  if (searching) return { bg: MATCH_PAIR.bg, fg: MATCH_PAIR.fg };
  return { bg: '#FFFFFF', fg: ink };
}

// WHICH WAY THE ROW CAN STILL SCROLL. A finger swipes; a mouse cannot, so where
// the tabs do not fit (a tablet held upright, a 1024 desk with the left-hand menu
// open) a mouse gets a button at each end that has more to show, instead of a
// scrollbar sitting between a tab and its sheet. One pixel of slack, because a
// browser reports fractional scroll positions as whole ones.
export function stripArrows({ scrollLeft = 0, clientWidth = 0, scrollWidth = 0 } = {}) {
  const over = scrollWidth > clientWidth + 1;
  return {
    left: over && scrollLeft > 1,
    right: over && scrollLeft + clientWidth < scrollWidth - 1,
  };
}

// THE PHONE ROW (M4): the chosen stage's tab, then the other six as paper edges
// in the order a transfer travels.
export function phoneEdges(tabs, chosenId) {
  return (tabs || []).filter(t => t.id !== chosenId);
}

// What the row says to a screen reader: where you are, and that a press opens
// the choice.
export function stageRowLabel(label, count) {
  return `Stage: ${label}${count > 0 ? `, ${count}` : ''}. Choose another stage`;
}
